import { Button, Spinner } from '@/features/common/ui';

/**
 * "Showing 50 invoices · Load more".
 *
 * ── WHY THE COUNT IS ALWAYS SHOWN, EVEN ON THE LAST PAGE ──
 * Because the failure this exists to fix was silence. A list that stopped at
 * fifty and said nothing was indistinguishable from a business that had fifty
 * invoices, and there was no way to reach the fifty-first. Printing the count
 * unconditionally means the number on screen is always the number of rows on
 * screen - so "showing 50" beside a Load more button and "showing 2,000" with
 * no button are both true statements rather than one of them being an absence.
 *
 * No page numbers, deliberately: a keyset cursor is "after this exact row", so
 * there is nothing to number. Offering page seven would mean an OFFSET query
 * underneath, which shifts under the reader as rows are added - the precise
 * problem the cursor exists to avoid.
 */
export function ListFooter({
  query,
  noun,
  one,
}: {
  query: {
    items: unknown[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
  };
  /** Plural, as it reads in "Showing 50 invoices". */
  noun: string;
  /** The singular, when it is not just the plural without its "s". */
  one?: string;
}) {
  const count = query.items.length;
  // "Showing 1 customers" is the kind of thing that makes an app feel unfinished
  // for the sake of one character. English pluralises on 1, not on 0.
  const label = count === 1 ? (one ?? noun.replace(/s$/, '')) : noun;

  return (
    <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3 text-sm">
      <span className="text-muted-foreground">
        Showing {count.toLocaleString('en-IN')} {label}
        {query.hasNextPage ? ' so far' : ''}
      </span>
      {query.hasNextPage ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
        >
          {query.isFetchingNextPage ? (
            <>
              <Spinner className="mr-1.5 size-3.5" /> Loading
            </>
          ) : (
            'Load more'
          )}
        </Button>
      ) : null}
    </div>
  );
}
